import {
  Button,
  Checkbox,
  Container,
  Form,
  Link,
  Stack,
  Text,
  useBasisForm,
} from "basis";
import { CreditCard } from "components/CreditCard/CreditCard";
import { FormActions } from "components/FormActions/FormActions";
import { Cancel } from "components/IncreaseCreditLimit/Cancel";
import { CancelLink } from "components/IncreaseCreditLimit/components/CancelLink";
import { Layout } from "components/Layout/Layout";
import { Separator } from "components/Separator/Separator";
import { formatAmount } from "library/moneyUtils";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCliContext } from "../hooks/useCliContext";
import { useGetSubmissionResult, useSubmit } from "../hooks/useSubmit";
import { nzValidateCheckboxField } from "../validation/nzCustomValidation";

export const ReviewDetails = () => {
  const { state } = useCliContext();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const initialValues = {
    agreeToConsent: false,
  };

  const { methods, Field } = useBasisForm<{ agreeToConsent: boolean }>({
    defaultValues: initialValues,
  });
  const { watch } = methods;
  const { agreeToConsent } = watch();

  const { accountInfo, newCreditLimit } = state;

  const { account } = state;
  const submitUrl = account?.links.find(
    (link) => link.rel === "credit_limit_increase",
  )?.href;

  const [submitSuccessfully, setSubmitSuccessfully] = useState<boolean>(false);
  const { isLoading: isSubmitting, mutate: submit } = useSubmit(state);
  const {
    isLoading: isGettingSubmissionResult,
    isError,
    applicationResponse,
  } = useGetSubmissionResult(`${submitUrl}/status`, submitSuccessfully);
  if (isError) {
    // TODO: will navigate to error page in RL-772
    navigate("/increase-credit-limit/pending-acceptance");
  }
  if (applicationResponse) {
    navigate("/increase-credit-limit/pending-acceptance");
  } else if (applicationResponse === null) {
    // TODO: will navigate to error page in RL-772
    navigate("/increase-credit-limit/pending-acceptance");
  }
  const onSubmit = () => {
    submit(
      { agreeToConsent },
      {
        onSuccess: () => {
          setSubmitSuccessfully(true);
        },
        onError: () => {
          // TODO: will navigate to error page in RL-772
          navigate("/increase-credit-limit/pending-acceptance");
        },
      },
    );
  };
  return (
    <Layout>
      <Layout.Header>Request a credit limit increase</Layout.Header>
      <Layout.Main>
        <Form methods={methods} onSubmit={onSubmit}>
          <Stack gap="8">
            <Container bg="grey.t05" padding="4">
              <CreditCard
                cardName={accountInfo?.cardName ?? ""}
                cardEndsWith={accountInfo?.cardEndsWith}
              />
            </Container>
            <Stack gap="4">
              <Text>
                <strong>Step 3 of 3</strong>
              </Text>
              <Text as="h3" textStyle="heading4">
                Review and submit
              </Text>
            </Stack>
            <Separator />
            <Container>
              <Text>I confirm that:</Text>
              <ul>
                <li>
                  Your contract with us is a general purpose credit card. By
                  applying for this credit limit increase, you confirm you
                  require the credit on an ongoing basis.
                </li>
                <li>
                  My credit limit will be increased to{" "}
                  {formatAmount(newCreditLimit)}, or a lesser credit limit that
                  Latitude Financial Services Limited is willing to provide me.
                </li>
                <li>
                  Latitude Financial Services Limited will complete a credit
                  check during my application to increase my credit limit.
                </li>
                <li>
                  The information I have provided in this application is correct
                  and I have not withheld any information which could cause
                  Latitude Financial Services Limited to incorrectly grant
                  credit to me.
                </li>
                <li>
                  I have read and agree to the{" "}
                  <Link
                    // TODO: RL-493 Need to update privacy notice link for NZ in the future, now using AU link
                    href="https://www.latitudefinancial.com.au/privacy"
                    newTab
                  >
                    Important Privacy Notice
                  </Link>
                  .
                </li>
                <li>
                  I understand that regardless of the outcome of my application,
                  or if I choose to cancel after submitting,
                  <br />I will need to wait at least 30 days before applying
                  again.
                </li>
              </ul>
            </Container>
            <Stack gap="8" width="100%" width-sm="400">
              <Field
                as={Checkbox}
                name="agreeToConsent"
                testId="agreeToConsent"
                label="Agree to all the above"
                hideLabel
                validate={nzValidateCheckboxField}
              >
                I have read and agree to all of the above
              </Field>
              <FormActions>
                <FormActions.Primary>
                  <Button
                    variant="primary"
                    type="submit"
                    loading={isSubmitting || isGettingSubmissionResult}
                  >
                    Submit
                  </Button>
                </FormActions.Primary>
                <FormActions.Secondary>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      navigate(-1);
                    }}
                  >
                    Back
                  </Button>
                </FormActions.Secondary>
              </FormActions>
              <CancelLink onClick={() => setIsModalOpen(true)} />
            </Stack>
            <Cancel
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onConfirm={() => {
                navigate("/manage-account");
              }}
            />
          </Stack>
        </Form>
      </Layout.Main>
    </Layout>
  );
};
